import { useState } from 'react';
import {
  Button,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
} from '@/components';
import { useGetTiers } from '@xhype/card-app-sdk';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import ReactJson from 'react-json-view';
import { z } from 'zod';

import { LoaderSkeleton } from './LoaderSkeleton';

/* Schemas */
export const BusinessTiersFormSchema = z.object({
  businessId: z
    .string()
    .min(1, { message: 'Business Id is required' })
    .refine((value) => !isNaN(Number(value)), {
      message: 'Business Id must be a valid number',
    }),
});

/* Types */
export type BusinessTiersFormType = z.infer<typeof BusinessTiersFormSchema>;

/* Components */
export const BusinessTiers = () => {
  const [businessId, setBusinessId] = useState<string | undefined>(undefined);

  /* Search Form */
  const searchForm = useForm<BusinessTiersFormType>({
    resolver: zodResolver(BusinessTiersFormSchema),
  });

  /* Handlers */
  const handleSearch = async (data: BusinessTiersFormType) => {
    setBusinessId(data.businessId);
  };

  /* Render */
  return (
    <section className='w-2/3 flex flex-col items-center justify-center gap-4'>
      <Form {...searchForm}>
        <form
          onSubmit={searchForm.handleSubmit(handleSearch)}
          className='w-full flex flex-col items-center justify-center gap-4'
        >
          <section className='w-full flex flex-row items-center justify-center gap-4'>
            <FormField
              control={searchForm.control}
              name='businessId'
              render={({ field }) => (
                <FormItem className='w-full'>
                  <FormLabel>Business Id</FormLabel>
                  <FormControl>
                    <Input placeholder='1' type='number' {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </section>
          <Button type='submit' className='cursor-pointer w-full'>
            Get deposit quote
          </Button>
        </form>
      </Form>
      {businessId && <GetBusinessTiers businessId={businessId} />}
    </section>
  );
};

/*Internal components */
type GetBusinessTiersProps = {
  businessId: string;
};

const GetBusinessTiers = ({ businessId }: GetBusinessTiersProps) => {
  const { isLoading, data: tiersData } = useGetTiers({
    businessId: Number(businessId),
    enabled: !!businessId,
  });

  /* Render */
  return (
    <section className='w-full flex flex-col items-center justify-center'>
      <section className='w-full flex flex-col items-center justify-center gap-4'>
        {!!isLoading && <LoaderSkeleton />}
        {!isLoading && tiersData && (
          <section className='w-full flex flex-col items-start justify-start gap-2'>
            <ReactJson collapsed src={tiersData || {}} />
          </section>
        )}
      </section>
    </section>
  );
};
